import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRAND, imageSrc } from "@/lib/site";
import { attribution, gaEvent, track } from "@/lib/track";

interface Card { id: string; name: string; price: number }
interface Product { id: string; name: string; price: number; intro: string; included: string[]; papers: string[]; previews: number; has_thumb: boolean; etsy_url?: string | null;
  product_type?: string; collection?: string; tutorials: { slug: string; title: string; image?: string | null }[]; related: Card[]; bundle_items: Card[]; in_bundles: Card[] }

function Gallery({ p }: { p: Product }) {
  const imgs = [...(p.has_thumb ? [`${API}/public/thumb/${p.id}`] : []), ...Array.from({ length: p.previews }, (_, i) => `${API}/public/listing-preview/${p.id}/${i}`)];
  const [sel, setSel] = useState(0);
  if (!imgs.length) return null;
  return (
    <div data-testid="product-gallery">
      <img src={imgs[sel]} alt={p.name} className="aspect-[5/4] w-full animate-none rounded-2xl bg-[#F3EEE6] object-contain shadow-xl" data-testid="product-main-image" />
      <div className="mt-3 flex gap-2 overflow-x-auto">
        {imgs.map((src, i) => (
          <button key={src} onClick={() => setSel(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-white transition-colors ${i === sel ? "border-[#D9785C]" : "border-transparent"}`} data-testid={`product-thumb-${i}`}>
            <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
}

function BuyButtons({ p }: { p: Product }) {
  const [busy, setBusy] = useState("");
  const [paypal, setPaypal] = useState(false);
  useEffect(() => { api.get("/shop/config").then((r) => setPaypal(r.data.paypal)).catch(() => {}); }, []);
  const go = async (provider: string) => {
    setBusy(provider);
    try {
      const r = await api.post(`/shop/checkout/${provider}`, { listing_id: p.id, origin_url: window.location.origin, ...attribution() });
      gaEvent("begin_checkout", { currency: "USD", value: p.price, items: [{ item_id: p.id, item_name: p.name, price: p.price }] });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  return (
    <div className="mt-8 space-y-3">
      <button onClick={() => go("stripe")} disabled={!!busy} className="btn-solid w-full justify-center sm:w-auto" data-testid="buy-stripe-btn">
        {busy === "stripe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Buy now - ${p.price.toFixed(2)}
      </button>
      {paypal && (
        <button onClick={() => go("paypal")} disabled={!!busy} className="btn-line w-full justify-center sm:ml-3 sm:w-auto" data-testid="buy-paypal-btn">
          {busy === "paypal" && <Loader2 className="h-4 w-4 animate-spin" />} Pay with PayPal
        </button>
      )}
      <p className="text-xs text-[#23324A]/60">Secure checkout. Instant download after payment, plus an email with your links.</p>
      {p.etsy_url && <a href={p.etsy_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#23324A] underline" data-testid="product-etsy-link">Prefer Etsy? Buy it there <ExternalLink className="h-3.5 w-3.5" /></a>}
    </div>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [p, setP] = useState<Product | null | undefined>(undefined);
  useEffect(() => { api.get(`/public/listings/${id}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [id]);
  useEffect(() => {
    if (!p) { return; }
    track("product_view", window.location.pathname, p.id);
    gaEvent("view_item", { currency: "USD", value: p.price, items: [{ item_id: p.id, item_name: p.name, price: p.price }] });
  }, [p]);
  usePageMeta(p ? `${p.name} | Printable | ${BRAND}` : `Printable | ${BRAND}`, p?.intro?.slice(0, 155) || "Instant-download printable gift kit.");

  if (p === undefined) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#23324A]/50" /></div>;
  if (p === null) return <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="product-missing"><p className="font-heading text-2xl">This printable isn't available.</p><Link to="/shop" className="btn-line mt-6">Back to the shop</Link></div>;
  return (
    <div className="mx-auto max-w-6xl px-5 pt-10 sm:px-8" data-testid="product-page">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-semibold text-[#23324A]/70 hover:text-[#23324A]" data-testid="product-back-link"><ArrowLeft className="h-4 w-4" /> All printables</Link>
      {params.get("canceled") && <p className="mt-4 rounded-xl bg-[#FBEFE9] px-4 py-3 text-sm text-[#23324A]" data-testid="product-canceled-note">Checkout was canceled. You haven't been charged.</p>}
      <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_1fr]">
        <Gallery p={p} />
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#23324A]/55" data-testid="product-eyebrow">{[p.product_type || "Printable gift kit", p.collection].filter(Boolean).join(" · ")}</p>
          <h1 className="font-heading text-4xl font-semibold text-[#23324A] sm:text-5xl" data-testid="product-name">{p.name}</h1>
          <p className="mt-3 font-heading text-2xl font-semibold text-[#D9785C]" data-testid="product-price">${p.price.toFixed(2)}</p>
          {p.intro && <p className="mt-5 text-[#23324A]/80" data-testid="product-intro">{p.intro}</p>}
          {p.included.length > 0 && (
            <ul className="mt-6 space-y-2 text-sm text-[#23324A]" data-testid="product-included">
              {p.included.map((t) => <li key={t} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D9785C]" strokeWidth={3} />{t}</li>)}
            </ul>
          )}
          <p className="mt-6 text-sm text-[#23324A]/70">Digital download: {p.papers.map((x) => (x === "a4" ? "A4" : "US Letter")).join(" + ")} PDFs. Nothing is shipped.</p>
          <BuyButtons p={p} />
        </div>
      </div>
      <ProductLinks p={p} />
    </div>
  );
}

function CardRow({ title, items, testId }: { title: string; items: Card[]; testId: string }) {
  if (!items.length) return null;
  return (
    <div data-testid={testId}>
      <h2 className="font-heading text-2xl font-semibold text-[#23324A]">{title}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {items.map((r) => (
          <Link key={r.id} to={`/shop/${r.id}`} className="calm-card group block overflow-hidden" data-testid={`${testId}-${r.id}`}>
            <img src={`${API}/public/thumb/${r.id}`} alt={r.name} className="aspect-[5/4] w-full object-cover" loading="lazy" />
            <div className="flex items-start justify-between gap-3 p-3"><p className="font-semibold leading-tight text-[#23324A]">{r.name}</p><p className="font-semibold text-[#D9785C]">${r.price.toFixed(2)}</p></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ProductLinks({ p }: { p: Product }) {
  return (
    <div className="mt-20 space-y-14">
      {p.tutorials.length > 0 && (
        <div data-testid="product-tutorials">
          <h2 className="font-heading text-2xl font-semibold text-[#23324A]">Make it with the tutorial</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {p.tutorials.map((t) => (
              <li key={t.slug}><Link to={`/${t.slug}`} className="calm-card flex items-center gap-4 p-3" data-testid={`product-tutorial-${t.slug}`}>
                {t.image && <img src={imageSrc(t.image)} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" loading="lazy" />}
                <span className="font-semibold text-[#23324A]">{t.title}</span>
              </Link></li>
            ))}
          </ul>
        </div>
      )}
      <CardRow title="Everything in this bundle" items={p.bundle_items} testId="product-bundle-items" />
      <CardRow title="Save with the bundle" items={p.in_bundles} testId="product-in-bundles" />
      <CardRow title="Complete the set" items={p.related} testId="product-related" />
    </div>
  );
}
